<template>
  <v-container fluid>



    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <div>
              <h2 class="text-h4 font-weight-bold">RAPS Management</h2>
              <p class="text-subtitle-1 text--secondary mb-0">
                Upload, processing and generation of RAPS files
              </p>
            </div>
            <v-btn
              color="primary"
              large
              @click="showUploadDialog = true"
            >
              <v-icon left>mdi-upload</v-icon>
              Upload RAPS File
            </v-btn>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>

    <!-- Estatísticas -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card color="primary" dark>
          <v-card-text class="d-flex align-center">
            <v-icon size="40" class="mr-3">mdi-file-document-multiple</v-icon>
            <div>
              <div class="text-h4 font-weight-bold">{{ stats.totalFiles }}</div>
              <div class="text-subtitle-2">Total Files</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card color="success" dark>
          <v-card-text class="d-flex align-center">
            <v-icon size="40" class="mr-3">mdi-check-circle</v-icon>
            <div>
              <div class="text-h4 font-weight-bold">{{ stats.processedFiles }}</div>
              <div class="text-subtitle-2">Processed</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card color="warning" dark>
          <v-card-text class="d-flex align-center">
            <v-icon size="40" class="mr-3">mdi-clock</v-icon>
            <div>
              <div class="text-h4 font-weight-bold">{{ stats.pendingFiles }}</div>
              <div class="text-subtitle-2">Pending</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card color="error" dark>
          <v-card-text class="d-flex align-center">
            <v-icon size="40" class="mr-3">mdi-alert-circle</v-icon>
            <div>
              <div class="text-h4 font-weight-bold">{{ stats.errorFiles }}</div>
              <div class="text-subtitle-2">With Error</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filtros -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="filters.status"
                  :items="statusOptions"
                  label="Status"
                  clearable
                  @change="loadRAPSFiles"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="filters.search"
                  label="Search by file name"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  @input="debounceSearch"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filters.dateRange"
                      label="Period"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filters.dateRange"
                    range
                    @change="loadRAPSFiles"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6" md="3" class="d-flex align-center">
                <v-btn
                  color="primary"
                  @click="loadRAPSFiles"
                  class="mr-2"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
                <v-btn
                  color="secondary"
                  @click="clearFilters"
                >
                  <v-icon left>mdi-filter-remove</v-icon>
                  Clear
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabela de Arquivos RAPS -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <span class="text-h6">RAPS Files</span>
            <v-spacer></v-spacer>
            <v-chip
              v-if="rapsFiles.length > 0"
              color="primary"
              text-color="white"
            >
              {{ rapsFiles.length }} file(s)
            </v-chip>
          </v-card-title>
          <v-data-table
            :headers="headers"
            :items="rapsFiles"
            :loading="loading"
            :items-per-page="10"
            class="elevation-1"
          >
            <!-- Status -->
            <template v-slot:item.status="{ item }">
              <v-chip
                :color="getStatusColor(item.status)"
                text-color="white"
                small
              >
                {{ getStatusText(item.status) }}
              </v-chip>
            </template>

            <!-- Estatísticas -->
            <template v-slot:item.totalRecords="{ item }">
              <div class="text-center">
                <div class="font-weight-bold">{{ item.totalRecords }}</div>
                <div class="text-caption text--secondary">Total</div>
              </div>
            </template>

            <template v-slot:item.recordsProcessed="{ item }">
              <div class="text-center">
                <div class="font-weight-bold text-success">{{ item.recordsProcessed || 0 }}</div>
                <div class="text-caption text--secondary">Processed</div>
              </div>
            </template>

            <template v-slot:item.recordsSkipped="{ item }">
              <div class="text-center">
                <div class="font-weight-bold text-error">{{ item.recordsSkipped || 0 }}</div>
                <div class="text-caption text--secondary">Skipped</div>
              </div>
            </template>

            <template v-slot:item.pendingRecords="{ item }">
              <div class="text-center">
                <div class="font-weight-bold text-warning">{{ item.pendingRecords }}</div>
                <div class="text-caption text--secondary">Pending</div>
              </div>
            </template>

            <template v-slot:item.submittedRecords="{ item }">
              <div class="text-center">
                <div class="font-weight-bold text-success">{{ item.submittedRecords }}</div>
                <div class="text-caption text--secondary">Submitted</div>
              </div>
            </template>

            <template v-slot:item.errorRecords="{ item }">
              <div class="text-center">
                <div class="font-weight-bold text-error">{{ item.errorRecords }}</div>
                <div class="text-caption text--secondary">Errors</div>
              </div>
            </template>

            <!-- Data de Upload -->
            <template v-slot:item.uploadedAt="{ item }">
              {{ formatDate(item.uploadedAt) }}
            </template>

            <!-- Ações -->
            <template v-slot:item.actions="{ item }">
              <v-menu bottom left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="viewDetails(item)">
                    <v-list-item-icon>
                      <v-icon>mdi-eye</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>View Details</v-list-item-title>
                  </v-list-item>
                  
                  <v-list-item 
                    @click="viewRecords(item)"
                    :disabled="item.status === 'uploaded'"
                  >
                    <v-list-item-icon>
                      <v-icon>mdi-table</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>View Records</v-list-item-title>
                  </v-list-item>
                  
                  <v-divider></v-divider>
                  
                  <v-list-item 
                    @click="processFile(item)"
                    :disabled="item.status !== 'uploaded'"
                  >
                    <v-list-item-icon>
                      <v-icon>mdi-cog</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Process</v-list-item-title>
                  </v-list-item>
                  
                  <v-list-item 
                    @click="generateFile(item)"
                    :disabled="item.status !== 'processed' || item.pendingRecords === 0"
                  >
                    <v-list-item-icon>
                      <v-icon>mdi-file-export</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Generate RAPS</v-list-item-title>
                  </v-list-item>
                  
                  <v-list-item 
                    @click="uploadReturn(item)"
                    :disabled="!item.generatedFileName"
                  >
                    <v-list-item-icon>
                      <v-icon>mdi-upload</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Upload Return</v-list-item-title>
                  </v-list-item>
                  
                  <v-divider></v-divider>
                  
                  <v-list-item 
                    @click="downloadFile(item, 'generated')"
                    :disabled="!item.generatedFileName"
                  >
                    <v-list-item-icon>
                      <v-icon>mdi-download</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Download RAPS</v-list-item-title>
                  </v-list-item>
                  
                  <v-list-item 
                    @click="downloadFile(item, 'return')"
                    :disabled="!item.returnFileName"
                  >
                    <v-list-item-icon>
                      <v-icon>mdi-download</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Download Return</v-list-item-title>
                  </v-list-item>
                  
                  <v-divider></v-divider>
                  
                  <v-list-item 
                    @click="deleteFile(item)"
                    class="error--text"
                  >
                    <v-list-item-icon>
                      <v-icon color="error">mdi-delete</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog de Upload -->
    <v-dialog v-model="showUploadDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">Upload RAPS File</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="uploadForm" v-model="uploadFormValid">
            <v-file-input
              v-model="uploadFile"
              label="Select CSV file"
              accept=".csv"
              :rules="[v => !!v || 'File is required']"
              show-size
              truncate-length="50"
              :disabled="uploading"
            ></v-file-input>
            <v-alert
              type="info"
              text
              class="mt-3"
              v-if="!uploading"
            >
              <strong>Expected format:</strong> CSV file with columns: AccountNumer, ICD10DXCode, OccuranceDateTime, etc.
            </v-alert>
            
            <!-- Indicador de Progresso -->
            <v-card v-if="uploading" class="mt-3" outlined>
              <v-card-text>
                <div class="text-center mb-3">
                  <v-progress-circular
                    :size="70"
                    :width="7"
                    color="primary"
                    indeterminate
                  ></v-progress-circular>
                </div>
                <div class="text-center">
                  <p class="text-h6 mb-2">{{ uploadStatus }}</p>
                  <p class="text-caption grey--text">Please wait. Do not close this window.</p>
                  
                  <!-- Contadores em tempo real -->
                  <v-row v-if="uploadCounters.totalRecords > 0" class="mt-3">
                    <v-col cols="4" class="text-center">
                      <div class="text-h6 text-primary">{{ uploadCounters.totalRecords }}</div>
                      <div class="text-caption">Total</div>
                    </v-col>
                    <v-col cols="4" class="text-center">
                      <div class="text-h6 text-success">{{ uploadCounters.recordsProcessed }}</div>
                      <div class="text-caption">Processed</div>
                    </v-col>
                    <v-col cols="4" class="text-center">
                      <div class="text-h6 text-warning">{{ uploadCounters.recordsSkipped }}</div>
                      <div class="text-caption">Skipped</div>
                    </v-col>
                  </v-row>
                </div>
                <v-divider class="my-3"></v-divider>
                <v-list dense>
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon :color="uploadSteps.upload ? 'success' : 'grey'">
                        {{ uploadSteps.upload ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>File upload</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon :color="uploadSteps.process ? 'success' : 'grey'">
                        {{ uploadSteps.process ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Data processing</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon :color="uploadSteps.generate ? 'success' : 'grey'">
                        {{ uploadSteps.generate ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>RAPS file generation</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="closeUploadDialog"
            :disabled="uploading"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="uploadRAPSFile"
            :loading="uploading"
            :disabled="!uploadFormValid || uploading"
          >
            <v-icon left>mdi-upload</v-icon>
            Upload and Process
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Detalhes -->
    <v-dialog v-model="showDetailsDialog" max-width="800px">
      <v-card>
        <v-card-title>
          <span class="text-h5">RAPS File Details</span>
        </v-card-title>
        <v-card-text>
          <v-row v-if="selectedFile">
            <v-col cols="12" sm="6">
              <v-text-field
                label="File Name"
                :value="selectedFile.originalFileName"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="File ID"
                :value="selectedFile.fileId"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Status"
                :value="getStatusText(selectedFile.status)"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Upload Date"
                :value="formatDate(selectedFile.uploadedAt)"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Total Records"
                :value="selectedFile.totalRecords"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Pending Records"
                :value="selectedFile.pendingRecords"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Submitted Records"
                :value="selectedFile.submittedRecords"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Error Records"
                :value="selectedFile.errorRecords"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Generated File"
                :value="selectedFile.generatedFileName || 'Not generated'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Generation Date"
                :value="selectedFile.generatedAt ? formatDate(selectedFile.generatedAt) : 'Not generated'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="showDetailsDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Upload de Retorno -->
    <v-dialog v-model="showReturnUploadDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">Upload RAPS Return File</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="returnUploadForm">
            <v-file-input
              v-model="returnFile"
              label="Select return file"
              accept=".rpt"
              :rules="[v => !!v || 'File is required']"
              show-size
              truncate-length="50"
              :disabled="uploadingReturn"
            ></v-file-input>
            <v-alert
              type="info"
              text
              class="mt-3"
            >
              <strong>Expected format:</strong> RPT return file with AAA, BBB, CCC, YYY and ZZZ records.
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="closeReturnUploadDialog"
            :disabled="uploadingReturn"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="uploadReturnFile"
            :loading="uploadingReturn"
            :disabled="!returnFile || uploadingReturn"
          >
            <v-icon left>mdi-upload</v-icon>
            Upload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificações -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  name: 'RAPS',
  data() {
    return {
      loading: false,
      uploading: false,
      uploadStatus: '',
      uploadSteps: {
        upload: false,
        process: false,
        generate: false
      },
      uploadCounters: {
        totalRecords: 0,
        recordsProcessed: 0,
        recordsSkipped: 0
      },
      pollingInterval: null,
      currentUploadId: null,
      rapsFiles: [],
      selectedFile: null,
      showUploadDialog: false,
      showDetailsDialog: false,
      showRecordsDialog: false,
      showReturnUploadDialog: false,
      uploadFormValid: false,
      uploadFile: null,
      returnFile: null,
      uploadingReturn: false,
      dateMenu: false,
      
      // Filtros
      filters: {
        status: null,
        search: '',
        dateRange: []
      },
      
      // Estatísticas
      stats: {
        totalFiles: 0,
        processedFiles: 0,
        pendingFiles: 0,
        errorFiles: 0
      },
      
      // Opções de status
      statusOptions: [
        { text: 'Uploaded', value: 'uploaded' },
        { text: 'Processing', value: 'processing' },
        { text: 'Processed', value: 'processed' },
        { text: 'Error', value: 'error' }
      ],
      
      // Headers da tabela
      headers: [
        { text: 'File Name', value: 'originalFileName', sortable: true },
        { text: 'File ID', value: 'fileId', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Total', value: 'totalRecords', sortable: true },
        { text: 'Processed', value: 'recordsProcessed', sortable: true },
        { text: 'Skipped', value: 'recordsSkipped', sortable: true },
        { text: 'Pending', value: 'pendingRecords', sortable: true },
        { text: 'Submitted', value: 'submittedRecords', sortable: true },
        { text: 'Errors', value: 'errorRecords', sortable: true },
        { text: 'Upload Date', value: 'uploadedAt', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, width: '100px' }
      ],
      
      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 5000
      }
    };
  },
  
  computed: {
    isSuperuser() {
      const user = this.$store.getters.user;
      return user && (user.role === 'Superuser' || user.role === 'Admin');
    }
  },
  
  mounted() {
    this.loadRAPSFiles();
  },
  
  beforeDestroy() {
    // Limpar polling quando componente for destruído
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  },
  
  methods: {
    async loadRAPSFiles() {
      this.loading = true;
      try {
        const response = await axios.get('/api/raps');
        this.rapsFiles = response.data;
        this.calculateStats();
      } catch (error) {
        console.error('Error loading RAPS files:', error);
        this.showSnackbar('Error loading RAPS files', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    calculateStats() {
      this.stats.totalFiles = this.rapsFiles.length;
      this.stats.processedFiles = this.rapsFiles.filter(f => f.status === 'processed').length;
      this.stats.pendingFiles = this.rapsFiles.filter(f => f.status === 'uploaded' || f.status === 'processing').length;
      this.stats.errorFiles = this.rapsFiles.filter(f => f.status === 'error').length;
    },
    
    async uploadRAPSFile() {
      // Proteção contra uploads múltiplos
      if (this.uploading) {
        this.showSnackbar('Upload already in progress. Please wait...', 'warning');
        return;
      }
      
      if (!this.uploadFile) {
        this.showSnackbar('Select a file', 'error');
        return;
      }
      
      this.uploading = true;
      
      // Resetar progresso
      this.uploadSteps = {
        upload: false,
        process: false,
        generate: false
      };
      this.uploadCounters = {
        totalRecords: 0,
        recordsProcessed: 0,
        recordsSkipped: 0
      };
      
      // Limpar polling anterior se existir
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
      
      const formData = new FormData();
      formData.append('rapsFile', this.uploadFile);
      
      try {
        // Etapa 1: Upload
        this.uploadStatus = 'Uploading file...';
        this.uploadSteps.upload = false;
        
        const response = await axios.post('/api/raps/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Marcar upload como concluído
        this.uploadStatus = 'Upload completed! Processing data...';
        this.uploadSteps.upload = true;
        
        // Iniciar polling para verificar progresso
        const rapsFileId = response.data.rapsFile.id;
        this.currentUploadId = rapsFileId;
        this.startPolling(rapsFileId);
        
      } catch (error) {
        console.error('Error uploading file:', error);
        this.showSnackbar('Error uploading file', 'error');
        this.resetUploadState();
      }
    },
    
        startPolling(rapsFileId) {
          const maxAttempts = 120; // 120 tentativas (2 minutos)
          let attempts = 0;
      
      const poll = async () => {
        // Verificar se ainda é o upload atual
        if (this.currentUploadId !== rapsFileId) {
          console.log('Polling cancelado - novo upload iniciado');
          return;
        }
        
        try {
          // Adicionar timestamp para evitar cache
          const timestamp = Date.now();
          const response = await axios.get(`/api/raps/${rapsFileId}/status?t=${timestamp}`);
          const status = response.data;
          
          // Atualizar contadores em tempo real
          this.uploadCounters.totalRecords = status.totalRecords || 0;
          this.uploadCounters.recordsProcessed = status.recordsProcessed || 0;
          this.uploadCounters.recordsSkipped = status.recordsSkipped || 0;
          
          // Atualizar progresso baseado no status
          if (status.status === 'processing') {
            this.uploadStatus = 'Processing data...';
            this.uploadSteps.process = false;
            this.uploadSteps.generate = false;
          } else if (status.status === 'processed' && !status.generatedFileName) {
            this.uploadStatus = 'Generating RAPS file...';
            this.uploadSteps.process = true;
            this.uploadSteps.generate = false;
          } else if (status.status === 'processed' && status.generatedFileName) {
            this.uploadStatus = 'Completed!';
            this.uploadSteps.process = true;
            this.uploadSteps.generate = true;
            
            // Mostrar resultado final
            this.showSnackbar(
              `File processed successfully! ${status.recordsProcessed} records processed, ${status.recordsSkipped} skipped.`,
              'success'
            );
            
            // Limpar polling
            if (this.pollingInterval) {
              clearInterval(this.pollingInterval);
              this.pollingInterval = null;
            }
            
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Resetar estado antes de fechar
            this.resetUploadState();
            this.closeUploadDialog();
            this.loadRAPSFiles();
            return;
          } else if (status.status === 'error') {
            this.uploadStatus = 'Processing error';
            this.showSnackbar(`Error: ${status.errorMessage}`, 'error');
            
            // Limpar polling
            if (this.pollingInterval) {
              clearInterval(this.pollingInterval);
              this.pollingInterval = null;
            }
            
            // Resetar estado antes de fechar
            this.resetUploadState();
            this.closeUploadDialog();
            return;
          }
          
          // Continuar polling se ainda está processando
          if (status.status === 'processing' || (status.status === 'processed' && !status.generatedFileName)) {
            attempts++;
            if (attempts < maxAttempts) {
              this.pollingInterval = setTimeout(poll, 1000); // Poll a cada 1 segundo
            } else {
              this.uploadStatus = 'Timeout - processing took too long';
              this.showSnackbar('Processing took too long', 'warning');
              
              // Limpar polling
              if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
              }
              
              // Resetar estado antes de fechar
              this.resetUploadState();
              this.closeUploadDialog();
            }
          }
          
        } catch (error) {
          console.error('Error checking status:', error);
          this.uploadStatus = 'Error checking progress';
          this.showSnackbar('Error checking progress', 'error');
          
          // Limpar polling
          if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
          }
          
          // Resetar estado antes de fechar
          this.resetUploadState();
          this.closeUploadDialog();
        }
      };
      
      // Iniciar polling
      this.pollingInterval = setTimeout(poll, 1000);
    },
    
    resetUploadState() {
      this.uploading = false;
      this.uploadStatus = '';
      this.uploadSteps = {
        upload: false,
        process: false,
        generate: false
      };
      this.uploadCounters = {
        totalRecords: 0,
        recordsProcessed: 0,
        recordsSkipped: 0
      };
      this.currentUploadId = null;
      
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    
    async processFile(file) {
      try {
        await axios.post(`/api/raps/${file._id}/process`);
        this.showSnackbar('File processed successfully!', 'success');
        this.loadRAPSFiles();
      } catch (error) {
        console.error('Error processing file:', error);
        this.showSnackbar('Error processing file', 'error');
      }
    },
    
    async generateFile(file) {
      try {
        const response = await axios.post(`/api/raps/${file._id}/generate`);
        this.showSnackbar('RAPS file generated successfully!', 'success');
        this.loadRAPSFiles();
      } catch (error) {
        console.error('Error generating file:', error);
        this.showSnackbar('Error generating RAPS file', 'error');
      }
    },
    
    async downloadFile(file, type) {
      try {
        const response = await axios.get(`/api/raps/${file._id}/download/${type}`, {
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', type === 'generated' ? file.generatedFileName : file.returnFileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        this.showSnackbar('Download started!', 'success');
      } catch (error) {
        console.error('Error downloading file:', error);
        this.showSnackbar('Error downloading file', 'error');
      }
    },
    
    async deleteFile(file) {
      if (!confirm(`Are you sure you want to delete the file "${file.originalFileName}"?`)) {
        return;
      }
      
      try {
        await axios.delete(`/api/raps/${file._id}`);
        this.showSnackbar('File deleted successfully!', 'success');
        this.loadRAPSFiles();
      } catch (error) {
        console.error('Error deleting file:', error);
        this.showSnackbar('Error deleting file', 'error');
      }
    },
    
    viewDetails(file) {
      this.selectedFile = file;
      this.showDetailsDialog = true;
    },
    
    viewRecords(file) {
      this.$router.push(`/raps/${file._id}/records`);
    },
    
    uploadReturn(file) {
      this.selectedFile = file;
      this.showReturnUploadDialog = true;
    },
    
    async uploadReturnFile() {
      if (!this.returnFile) {
        this.showSnackbar('Select a file', 'error');
        return;
      }
      
      this.uploadingReturn = true;
      
      const formData = new FormData();
      formData.append('returnFile', this.returnFile);
      
      try {
        const response = await axios.post(`/api/raps/${this.selectedFile._id}/upload-return`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        const { correctedCount, errorCount, notFoundCount, totalRecords, matchedRecords } = response.data;
        
        let message = `Return file processed!\n`;
        message += `• ${matchedRecords} of ${totalRecords} records matched\n`;
        message += `• ${correctedCount} corrected\n`;
        message += `• ${errorCount} with error`;
        
        if (notFoundCount > 0) {
          message += `\n⚠️ ${notFoundCount} records not found in original file`;
        }
        
        this.showSnackbar(message, 'success');
        
        this.closeReturnUploadDialog();
        this.loadRAPSFiles();
      } catch (error) {
        console.error('Error uploading return file:', error);
        this.showSnackbar('Error uploading return file', 'error');
      } finally {
        this.uploadingReturn = false;
      }
    },
    
    closeReturnUploadDialog() {
      this.showReturnUploadDialog = false;
      this.returnFile = null;
      
      if (this.$refs.returnUploadForm) {
        this.$refs.returnUploadForm.reset();
      }
    },
    
    closeUploadDialog() {
      // Se está fazendo upload, não permitir fechar
      if (this.uploading) {
        this.showSnackbar('Please wait for upload to finish before closing', 'warning');
        return;
      }
      
      this.showUploadDialog = false;
      this.uploadFile = null;
      this.resetUploadState();
      
      if (this.$refs.uploadForm) {
        this.$refs.uploadForm.reset();
      }
    },
    
    clearFilters() {
      this.filters = {
        status: null,
        search: '',
        dateRange: []
      };
      this.loadRAPSFiles();
    },
    
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.loadRAPSFiles();
      }, 500);
    },
    
    getStatusColor(status) {
      const colors = {
        uploaded: 'blue',
        processing: 'orange',
        processed: 'green',
        error: 'red'
      };
      return colors[status] || 'grey';
    },
    
    getStatusText(status) {
      const texts = {
        uploaded: 'Uploaded',
        processing: 'Processing',
        processed: 'Processed',
        error: 'Error'
      };
      return texts[status] || 'Unknown';
    },
    
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    showSnackbar(message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color,
        timeout: 5000
      };
    }
  }
};
</script>

<style scoped>
.v-card {
  margin-bottom: 16px;
}

.v-data-table {
  border-radius: 4px;
}

.text-h4 {
  font-size: 2.125rem !important;
  font-weight: 400;
  line-height: 2.5rem;
  letter-spacing: 0.0073529412em !important;
}

.text-h6 {
  font-size: 1.25rem !important;
  font-weight: 500;
  line-height: 2rem;
  letter-spacing: 0.0125em !important;
}
</style>
